/**
 * McpRunOverlay — hace visible dentro de Studio Media Labs un workflow que
 * está ejecutando un agente (Claude Code, Codex, …).
 *
 * No bloquea nada: el usuario entra al proyecto y lo observa. Muestra el badge
 * "MCP activo · <agente>", el estado de cada nodo, créditos estimados vs
 * consumidos y el botón para detener la ejecución.
 *
 * Los colores se aplican con un <style> sobre los nodos de React Flow
 * (`data-id`), así no hace falta tocar el store ni los componentes de nodo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Ban, Bot, CircleDot, Loader2, OctagonPause, X, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '../../store/workflowStore';
import { mcpService } from '../../services/mcpService';

const POLL_MS = 3000;

// Nombre amigable del nodo que se está esperando al detener.
const NODE_LABEL = {
  videoWithSound: 'Video con Sonido',
  videoGeneration: 'Crear Video',
  seedance25: 'Seedance 2.5',
  pixverseV6: 'Pixverse V6',
  klingAvatar: 'Avatar Kling',
  fabricAvatar: 'Avatar Parlante',
  dreamActor: 'DreamActor',
  avatarFast: 'Avatar Rápido',
  nanoBanana: 'Nano Banana',
  banana2pro: 'Banana 2 Pro',
  imagen4Ultra: 'Imagen 4 Ultra',
  imageEnhancement: 'Mejorar Imagen',
  upscale: 'Upscale',
  bgRemoval: 'Remover Fondo',
  styleTransfer: 'Transferir Estilo',
  lipsync: 'LipSync',
  lyria2: 'Lyria 2',
  lyria3Pro: 'Lyria 3 Pro',
  musicPro: 'Música',
  qwen3tts: 'Qwen3 TTS',
};

const STATE_STYLE = {
  completed: 'outline:2px solid #10b981;',
  processing: 'outline:2px solid #3b82f6;animation:mcpNodePulse 1.5s ease-in-out infinite;',
  queued: 'outline:2px dashed #3b82f6;',
  failed: 'outline:2px solid #ef4444;',
  pending: 'outline:1px dashed #6b7280;',
  skipped: 'outline:1px dashed #6b7280;opacity:.6;',
  cancelled: 'outline:1px dashed #6b7280;opacity:.6;',
};

const RUN_LABEL = {
  queued: 'En cola',
  running: 'Ejecutando',
  cancelling: 'Deteniendo…',
  cancelled: 'Detenido',
  completed: 'Completado',
  partially_failed: 'Falló un nodo',
  budget_exceeded: 'Presupuesto agotado',
  timed_out: 'Cerrado por inactividad',
};

const nodeStyles = (steps) => steps
  .map((s) => {
    const css = STATE_STYLE[s.status];
    return css
      ? `.react-flow__node[data-id="${s.node_id}"]{${css}outline-offset:4px;border-radius:16px;}`
      : '';
  })
  .join('\n');

export const McpRunOverlay = () => {
  const projectId = useWorkflowStore((s) => s.currentProjectId);
  const loadProject = useWorkflowStore((s) => s.loadProject);
  const [run, setRun] = useState(null);
  // Envío LMX-T · todas las corridas vivas del proyecto (dos trabajos de LMX
  // pueden generar a la vez). El panel muestra la más nueva.
  const [runs, setRuns] = useState([]);
  const [stopping, setStopping] = useState(false);
  const [cancellingSince, setCancellingSince] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState(false);
  const [lastOutputs, setLastOutputs] = useState(0);

  useEffect(() => {
    if (!projectId) { setRun(null); setRuns([]); return undefined; }
    let alive = true;
    const tick = async () => {
      try {
        const res = await mcpService.getActiveRun(projectId);
        if (alive) {
          const all = res.runs || (res.run ? [res.run] : []);
          setRun(res.run || null);
          setRuns(all);
          // Envío LMX-6b · la columna de LMX muestra el avance (sin otro sondeo).
          window.dispatchEvent(new CustomEvent('sml:mcp-run', {
            detail: { projectId, run: res.run || null, runs: all },
          }));
        }
      } catch {
        if (alive) { setRun(null); setRuns([]); }
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [projectId]);

  // Cuando el orquestador produce un output nuevo, recargamos el proyecto para
  // que la imagen/video aparezca en el nodo igual que en una corrida manual.
  useEffect(() => {
    const done = runs.reduce((n, r) => n + (r.completed_nodes || []).length, 0);
    if (run && done > lastOutputs) {
      setLastOutputs(done);
      // Envío LMX-6c: merge seguro en vez de loadProject (que reemplazaba todo
      // el canvas y descartaba lo que el usuario tuviera sin guardar). Segundo
      // intento a los 4 s por si el resultado se escribió un poco después.
      const pid = projectId;
      const sync = () => {
        const ws = useWorkflowStore.getState();
        if (!pid || ws.currentProjectId !== pid) return;
        if (ws.syncFromAgent) ws.syncFromAgent();
        else loadProject(pid);
      };
      sync();
      setTimeout(sync, 4000);
    }
    if (!run) setLastOutputs(0);
  }, [run, runs, lastOutputs, projectId, loadProject]);

  // Envío LMX-4 (I-26): una corrida corta (un solo nodo) termina y deja de
  // estar «activa» entre dos sondeos, así que el contador de arriba nunca ve
  // el nodo completado. Al pasar de «hay corrida» a «no hay», se trae el
  // proyecto una vez más (merge a 3 vías: no pisa lo que el usuario tenga sin
  // guardar) y otra a los 4 s, por si el asset se registró un poco después.
  // Envío LMX-T · con dos corridas, cuando una cierra (y la otra sigue) también
  // se traen sus últimos resultados.
  const runIdsRef = useRef([]);
  useEffect(() => {
    const ids = runs.map((r) => r.run_id);
    const closed = runIdsRef.current.some((id) => !ids.includes(id));
    runIdsRef.current = ids;
    if (!closed || !ids.length || !projectId) return undefined;
    const sync = () => {
      const ws = useWorkflowStore.getState();
      if (ws.currentProjectId === projectId) ws.syncFromAgent?.();
    };
    sync();
    const t = setTimeout(sync, 4000);
    return () => clearTimeout(t);
  }, [runs, projectId]);

  const hadRunRef = useRef(false);
  useEffect(() => {
    if (run) { hadRunRef.current = true; return undefined; }
    if (!hadRunRef.current || !projectId) return undefined;
    hadRunRef.current = false;
    const refresh = () => {
      const ws = useWorkflowStore.getState();
      if (ws.currentProjectId !== projectId) return;
      if (ws.syncFromAgent) ws.syncFromAgent();
      else loadProject(projectId);
    };
    refresh();
    const t = setTimeout(refresh, 4000);
    return () => clearTimeout(t);
  }, [run, projectId, loadProject]);

  const stop = useCallback(async () => {
    if (!window.confirm(
      'Detener la ejecución del agente?\n\nNo se arrancan nodos nuevos y se '
      + 'conserva todo lo ya generado. Si un nodo está procesando, termina.'
    )) return;
    setStopping(true);
    try {
      const res = await mcpService.cancelRun(run.run_id);
      setRun(res.run);
      toast.success('Ejecución detenida');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo detener la ejecución');
    } finally { setStopping(false); }
  }, [run]);

  // "Deteniendo…": a los 20 s aparece "Forzar detención".
  useEffect(() => {
    if (run?.status !== 'cancelling') { setCancellingSince(null); return undefined; }
    if (!cancellingSince) setCancellingSince(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [run?.status, cancellingSince]);

  const forceStop = useCallback(async () => {
    if (!window.confirm(
      '¿Forzar la detención?\n\nSe corta la generación que está en curso. '
      + 'No se te cobra y el resultado se descarta.'
    )) return;
    setStopping(true);
    try {
      const res = await mcpService.forceStopRun(run.run_id);
      setRun(res.run);
      window.dispatchEvent(new Event('executions:changed'));
      toast.success(res.stopped_executions?.length
        ? `Detención forzada: ${res.stopped_executions.length} generación(es) cortada(s)`
        : 'Ejecución cerrada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo forzar la detención');
    } finally { setStopping(false); }
  }, [run]);

  if (!run) return null;
  const live = ['queued', 'running', 'cancelling'].includes(run.status);
  const steps = run.steps || [];
  const waitingFor = steps.find((s) => s.status === 'processing');
  const waitingSecs = waitingFor?.queued_at
    ? Math.max(0, Math.round((now - new Date(waitingFor.queued_at).getTime()) / 1000))
    : null;
  const canForce = run.status === 'cancelling' && cancellingSince
    && now - cancellingSince > 20000;

  return (
    <>
      <style>{nodeStyles(runs.length > 1 ? runs.flatMap((r) => r.steps || []) : steps)}</style>
      <div
        className={`mcp-run-overlay ${collapsed ? 'collapsed' : ''} status-${run.status}`}
        data-testid="mcp-run-overlay"
      >
        <button
          className="mcp-run-head"
          onClick={() => setCollapsed((c) => !c)}
          data-testid="mcp-run-badge"
        >
          <Bot size={14} />
          <span className="mcp-run-agent">
            MCP activo · {run.agent_name || run.client_name || 'agente'}
            {runs.length > 1 && ` · ${runs.length} corridas`}
          </span>
          <span className="mcp-run-status">{RUN_LABEL[run.status] || run.status}</span>
          {live && <Loader2 size={12} className="spin" />}
        </button>

        {!collapsed && (
          <div className="mcp-run-body">
            <div className="mcp-run-credits" data-testid="mcp-run-credits">
              <span>{run.credits_spent} / {run.credits_planned} créditos</span>
              <em data-testid="mcp-run-reserved">
                {live
                  ? `reservados: ${run.credits_reserved ?? run.credits_authorized}`
                  : `liberado: ${Math.max(
                    (run.credits_reserved ?? 0) - (run.credits_spent ?? 0), 0,
                  )}`}
              </em>
            </div>

            <ul className="mcp-run-steps" data-testid="mcp-run-steps">
              {steps.map((s) => (
                <li key={s.node_id} className={`step-${s.status}`}
                    data-testid={`mcp-run-step-${s.node_id}`}>
                  {s.status === 'completed' && <CheckCircle2 size={11} />}
                  {s.status === 'failed' && <AlertTriangle size={11} />}
                  {(s.status === 'processing' || s.status === 'queued')
                    && <Loader2 size={11} className="spin" />}
                  {['pending', 'skipped', 'cancelled'].includes(s.status)
                    && <CircleDot size={11} />}
                  <span className="mcp-step-node">{s.node_type}</span>
                  <span className="mcp-step-id">{s.node_id}</span>
                </li>
              ))}
            </ul>

            {run.error_message && (
              <p className="mcp-run-error" data-testid="mcp-run-error">
                <AlertTriangle size={11} /> {run.error_message}
              </p>
            )}

            {run.status === 'cancelling' && (
              <p className="mcp-run-waiting" data-testid="mcp-run-waiting">
                <Loader2 size={11} className="spin" />
                {waitingFor
                  ? `Esperando que termine ${NODE_LABEL[waitingFor.node_type] || waitingFor.node_type}`
                  : 'Cerrando la ejecución'}
                {waitingSecs !== null && ` · ${Math.floor(waitingSecs / 60)}:${String(waitingSecs % 60).padStart(2, '0')}`}
              </p>
            )}

            {live ? (
              <div className="mcp-run-actions">
                <button
                  className="mcp-run-stop"
                  onClick={stop}
                  disabled={stopping || run.status === 'cancelling'}
                  data-testid="mcp-run-stop"
                >
                  {stopping ? <Loader2 size={12} className="spin" /> : <OctagonPause size={12} />}
                  Detener ejecución
                </button>
                {canForce && (
                  <button
                    className="mcp-run-stop force"
                    onClick={forceStop}
                    disabled={stopping}
                    title="Corta la generación en curso. No se te cobra."
                    data-testid="mcp-run-force-stop"
                  >
                    <Ban size={12} /> Forzar detención
                  </button>
                )}
              </div>
            ) : (
              <button
                className="mcp-run-stop ghost"
                onClick={() => setRun(null)}
                data-testid="mcp-run-dismiss"
              >
                <X size={12} /> Cerrar
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default McpRunOverlay;
